import axios from "axios";
import { getSubdomain } from "../utils/subdomain";

axios.defaults.withCredentials = true;
const baseURL = "https://services.soochiway.com/api/";
// const baseURL = "http://127.0.0.1:3001/api/";
// const baseURL = "https://projectsoochibackend-production.up.railway.app/api/"; // Use this for Railway deployment

// --- Auth token (Bearer) support ---------------------------------------------
// The httpOnly `jwt` cookie is not sent on cross-site requests (frontend on
// *.localhost, API on 127.0.0.1) or when the browser blocks third-party cookies.
// As a fallback we also send the JWT returned by /users/login as a Bearer token.
const AUTH_TOKEN_KEY = "auth_token";

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
};

export const clearAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

export const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);

axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const clientId = getSubdomain();

const handleApiError = (error) => {
  return error.response ? error.response.data : "Error occurred";
};

// Generic API requests
export const getData = async (service) => {
  try {
    const response = await axios.get(
      `${baseURL}${service}?clientId=${clientId}`
    );
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getRevenueByDate = async (startDate, endDate) => {
  try {
    const response = await axios.get(`${baseURL}invoices/revenue-by-date`, {
      params: {
        startDate,
        endDate,
        clientId: clientId,
      },
    });
    return response.data;
  } catch (error) {
    return { error: "Failed to fetch revenue data" };
  }
};

export const getWeeklyRevenueStats = async () => {
  try {
    const response = await axios.get(
      `${baseURL}invoices/weekly-revenue?clientId=${clientId}`
    );
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getDataByID = async (service, id) => {
  try {
    const response = await axios.get(
      `${baseURL}${service}/${id}?clientId=${clientId}`
    );
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const createData = async (service, data) => {
  try {
    const create = await axios.post(
      `${baseURL}${service}?clientId=${clientId}`,
      data
    );
    return create;
  } catch (err) {
    return err.response;
  }
};

export const updateData = async (service, id, updateData) => {
  try {
    const create = await axios.put(
      `${baseURL}${service}/${id}?clientId=${clientId}`,
      updateData
    );
    return create;
  } catch (err) {
    return err.response;
  }
};

export const patchData = async (service, id, data) => {
  try {
    const response = await axios.patch(
      `${baseURL}${service}/${id}?clientId=${clientId}`,
      data
    );
    return response;
  } catch (err) {
    return err.response;
  }
};

export const deleteData = async (service, id) => {
  if (!id) {
    return { error: "ID is missing" };
  }

  try {
    const response = await axios.delete(
      `${baseURL}${service}/${id}?clientId=${clientId}`
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

export const getDataByQuery = async (service, data) => {
  try {
    const res = await axios.get(`${baseURL}${service}?clientId=${clientId}`, {
      params: data,
    });
    return res.data;
  } catch (err) {
    return err.response?.data || null;
  }
};

export const deleteInvoiceByID = async (service, invoiceNumber) => {
  if (!invoiceNumber) {
    return { error: "Invoice number is missing" };
  }

  const apiUrl = `${baseURL}${service}/${invoiceNumber}?clientId=${clientId}`;

  try {
    const response = await axios.delete(apiUrl);
    return response.data;
  } catch (error) {
    return error.response?.data || { error: "Unknown error occurred" };
  }
};

export const findUserByUsername = async (user_name, data) => {
  try {
    const response = await axios.get(
      `${baseURL}${user_name}?clientId=${clientId}`
    );
    return response.data;
  } catch (err) {
    return err.response ? err.response.data : "Error getting user";
  }
};
